import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { isType } from '@sorare/core/src/gql';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { cardAttributes } from '@sorare/core/src/lib/glossary';

import { PlayerScore as USPlayerScore } from 'components/card/USSports/PlayerScore';

import { RecommendationLastFifteen_anyPlayer } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: center;
  justify-content: center;
`;

type Props = {
  player: RecommendationLastFifteen_anyPlayer;
};

export const LastFifteen = ({ player }: Props) => {
  const {
    flags: { useMlbSeasonAverageScore },
  } = useFeatureFlags();

  //TODO: DEPRECATE WHEN US SPORTS ARE MIGRATED TO RAILS
  // linear https://linear.app/sorare/issue/MAR-715/clean-for-you-recommendation-components-lastfifteenand-lastgamewhen

  return (
    <Root>
      <BodyS as="span" color="var(--c-neutral-600)">
        {isType(player, 'Player') && 'L15'}
        {isType(player, 'NBAPlayer') && 'L10'}
        {isType(player, 'BaseballPlayer') &&
          (useMlbSeasonAverageScore ? (
            <FormattedMessage {...cardAttributes.season} />
          ) : (
            'L15'
          ))}
      </BodyS>
      {isType(player, 'Player') || isType(player, 'BaseballPlayer') ? (
        <PlayerScore
          player={player}
          mode={AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE}
        />
      ) : (
        <USPlayerScore score={player.tenGameAverageGameStats.score} />
      )}
    </Root>
  );
};

LastFifteen.fragments = {
  anyPlayer: gql`
    fragment RecommendationLastFifteen_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      ... on NBAPlayer {
        slug
        tenGameAverageGameStats {
          score
        }
      }
      ...PlayerScore_anyPlayer
    }
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<RecommendationLastFifteen_anyPlayer>,
};
